/** A host name that only a developer's machine serves. */
const LOCAL_HOSTNAME = /^(localhost|127\.0\.0\.1|\[::1\])$|(^|[.-])localhost(\.|$)/;

export default class BrandclubStoreConfigUtils {
  public static readonly stripeDebugMode = false;

  public static getAppStage(): 'localhost' | 'beta' | 'gamma' | 'prod' {
    // Use process.env.NODE_ENV for compatibility with Jest/Node environments
  // In Vite environments, import.meta.env.MODE would be available but we prioritize process.env.NODE_ENV
  const nodeEnv = process.env.NODE_ENV;

    if (nodeEnv === 'development') {
      return 'localhost';
    }

    return this.stageOfOrigin(window.location.origin);
  }

  /**
   * The stage that names where a shopper's session and guest cookie are kept. It reads only the page
   * address. Two bundles on one page can come from builds of different modes, and they must find the
   * same session.
   */
  public static getShopperIdentityStage(): 'localhost' | 'beta' | 'gamma' | 'prod' {
    return this.shopperIdentityStageOf(window.location.href);
  }

  public static shopperIdentityStageOf(url: string): 'localhost' | 'beta' | 'gamma' | 'prod' {
    const { hostname, origin } = new URL(url);
    return LOCAL_HOSTNAME.test(hostname) ? 'localhost' : this.stageOfOrigin(origin);
  }

  /** The deployed stage that an address names. An address with no stage mark is production. */
  private static stageOfOrigin(origin: string): 'beta' | 'gamma' | 'prod' {
    if (origin.includes('beta.brandclub') || origin.includes('-beta.') || origin.includes('beta-')) {
      return 'beta';
    }

    if (origin.includes('gamma.brandclub') || origin.includes('-gamma.') || origin.includes('gamma-')) {
      return 'gamma';
    }

    return 'prod';
  }

  private static hostWebAppId: string | undefined;

  /**
   * The host names the app this page belongs to. A Pages storefront page is served under a merchant's
   * domain or the Shopify App Proxy, where the hostname is not the app id.
   */
  public static setWebAppId(appId: string | undefined) {
    this.hostWebAppId = appId || undefined;
  }

  /** The app id the host set, else the legacy convention: the app id is the hostname. */
  public static getWebAppId(): string {
    return this.hostWebAppId ?? window.location.hostname;
  }

  /**
   * @param parentScriptName - The name of the script that is hosting the common-ui.
   * @returns The origin of the script that is hosting the common-ui.
   */
  static getScriptOrigin(parentScriptName?: string): string | null {
    const currentScript = document.currentScript as HTMLScriptElement;
    if (currentScript?.src) {
      return new URL(currentScript.src).hostname;
    }
    if (!parentScriptName) {
      return null;
    }
    // Fallback: Find the script by its distinctive path
    const findScript = (): HTMLScriptElement | null => {
      const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];

      for (const script of scripts) {
        if (script.src.includes(parentScriptName)) {
          return script;
        }
      }
      return null;
    };

    const script = findScript();
    if (script?.src) {
      try {
        return new URL(script.src).hostname; // Will return "shop.abc.com"
      } catch {
        return null;
      }
    }
    return null;
  }
  private static cookieDomain = "";
  private static storageDomain = "";

  public static getLocalStorageDomain(appStage: 'localhost' | 'beta' | 'gamma' | 'prod' = 'prod', {
    domainName,
  }: {
    domainName?: string;
  }) {
    if (this.storageDomain) {
      return this.storageDomain;
    }
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('brandclub.com')) {
      // For brandclub domains, use stage prefix (except prod or gamma)
      this.storageDomain = appStage === 'prod' ? 'brandclub.com' : `${appStage}.brandclub.com`;
    } else {
      // For third-party domains, prefer script origin (where checkout app is hosted) or hostname
      const scriptOrigin = domainName ?? this.getScriptOrigin();
      this.storageDomain = scriptOrigin || (hostname.startsWith('shop.') ? hostname : 'shop.' + hostname);
    }

    return this.storageDomain;
  }

  public static getCookieDomain(): string {
    if (this.cookieDomain) {
      return this.cookieDomain;
    }
    const hostname = window.location.hostname.toLowerCase();
    // Split the hostname and get the parts
    const parts = hostname.split('.');

    // If we have less than 2 parts, return the whole hostname
    if (parts.length < 2) {
      this.cookieDomain = hostname;
      return hostname;
    }
    // Extract base domain, handling multi-part TLDs
    const tld = parts[parts.length - 1];
    let cookieDomain: string;

    if (tld.length === 2 && parts.length >= 3) {
      // Country code TLD, likely has structure like .co.in, .com.au
      cookieDomain = parts.slice(-3).join('.');
    } else {
      // Generic TLD (.com, .org, .net, etc.) - take last 2 parts
      cookieDomain = parts.slice(-2).join('.');
    }

    this.cookieDomain = cookieDomain;
    return cookieDomain;
  }

  /**
   * Back-room "routePath" knob (Storefront Concierge Plan 3 / D-14).
   *
   * When the app is served on a brand's Shopify domain under a sub-path, the
   * proxy skeleton injects that sub-path as `window.__BC_ROUTE_PATH__` so that
   * same-origin API calls go through the App-Proxy forwarder
   * (`/apps/brandclub/api-rewards/*`) instead of escaping to a bare
   * `/api-rewards/*` that the storefront does not proxy. Absent (front-room) →
   * "" → today's root-relative behavior, unchanged.
   */
  public static getRoutePath(): string {
    if (typeof window === 'undefined') return '';
    const raw = (window as unknown as { __BC_ROUTE_PATH__?: string }).__BC_ROUTE_PATH__;
    if (typeof raw !== 'string') return '';
    const trimmed = raw.trim().replace(/\/+$/, '');
    if (!trimmed || trimmed === '/') return '';
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  }

  public static getApiHostName(): string {
    return `${window.location.origin}${this.getRoutePath()}/api-rewards`;
  }

  public static getServerSideSyncHostName(): string {
    // NOTE: only `/api-rewards/*` has an App-Proxy forwarder today (Plan 3).
    // `/api-retailer-sync/*` is not exercised by the concierge; a forwarder for
    // it (and other surfaces) is Plan 4. Prefixed for namespace consistency.
    return `${window.location.origin}${this.getRoutePath()}/api-retailer-sync`;
  }

  public static getCheckoutApiHostName(): string {
    // api-checkout serves the checkout surface under its own `/api-checkout`
    // prefix (extraction off api-retailer-sync). Same origin + route path as the
    // sync host; every consumer CloudFront distribution has a `/api-checkout/*`
    // behavior → the api-checkout service. All checkout API calls route here.
    return `${window.location.origin}${this.getRoutePath()}/api-checkout`;
  }

  public static getGoogleMapsApiKey(): string {
    return 'AIzaSyDIvKiFNOD6JC_3ZXMeE4rtPVw49BFanJg';
  }

  public static getGoogleMapsMapId(): string {
    return '6b6881fc70e2cb88'; // Created in google cloud console
  }

  public static getEdgeCachedApiHostName(): string {
    return `${window.location.origin}${this.getRoutePath()}/api-rewards`;
  }

  public static getGraphQlUri(): string {
    const base = this.getApiHostName();
    return `${base}/graphql`;
  }

  public static getStripePublishableKey(): string {
    return this.stripePublishableKeyFor(this.getAppStage());
  }

  /**
   * The platform's publishable key for a stage. It reads no `window`, so a page that also renders on the
   * server can ask for it with the stage it knows.
   */
  public static stripePublishableKeyFor(appStage: 'localhost' | 'beta' | 'gamma' | 'prod'): string {
    if (appStage === 'localhost' && this.stripeDebugMode) {
      return 'pk_test_51SiebcJilhDtJ9iZBhWkI73CyuOtQ883cAFhkILcf1Dh06NOJPAO1LE3su05gFSOgM4PoLcPTFtJyD8bErZbxSYu00BjOvTQAq';
    }

    return 'pk_live_51LnQyZJrInA7nkEZJwYqka6fFt6Zz8m1V3l4hC2Rn3N3iLXCWgd8K3oQonZw3SWdpZoZ1UPogaw3kQwt5DyWnVXz00Mr9PFmjh';
  }

  public static getServerSideSyncGraphQlUri(): string {
    const base = this.getServerSideSyncHostName();

    return `${base}/graphql`;
  }

  public static getCheckoutApiGraphQlUri(): string {
    // /api-checkout/graphql — the scoped checkout GraphQL surface (order history +
    // cart) on the api-checkout host. Used to migrate the checkout GraphQL reads
    // off /api-retailer-sync/graphql.
    return `${this.getCheckoutApiHostName()}/graphql`;
  }
}
